import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";

import { api } from "~/api";
import type { Cluster } from "~/types";
import { cn, getDisplayUsername } from "~/utils";
import { buildSetConfigCreateRequestBody } from "~/utils/setConfigOperation";

import { Button } from "./Button";
import { Icon } from "./icon";
import {
  countSettingsOverrides,
  getSettingsOverrideFiles,
  settingsOverridesEqual,
  type SettingsOverrides,
  YamlCodeBlock,
} from "./settings-overrides/helpers";
import { SettingsOverridesEditor } from "./settings-overrides/SettingsOverridesEditor";
import { SettingsOverridesPreviewDialog } from "./settings-overrides/SettingsOverridesPreviewDialog";
import { validateSettingsOverrides } from "./settings-overrides/validation";

export function SettingsOverridesSummary({
  settingsOverrides,
}: {
  settingsOverrides: Cluster["settings_overrides"];
}) {
  const count =
    settingsOverrides == null ? 0 : countSettingsOverrides(settingsOverrides);

  return (
    <div
      className={cn(
        "group/overrides relative flex items-center gap-1 text-sm",
        count === 0 ? "text-muted-foreground" : "text-orange-500",
      )}
      aria-label={`${count} settings overrides`}
    >
      <Icon name="Cog" size={14} />
      <span className="tabular-nums">{count}</span>
      <span className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow transition-opacity group-hover/overrides:visible group-hover/overrides:opacity-100">
        {count} settings overrides
      </span>
    </div>
  );
}

// The selected file tab is shared by the viewer and the editor so that
// toggling between them keeps showing the same file.
interface SettingsOverridesFileSelection {
  selectedFile: string | null;
  setSelectedFile: Dispatch<SetStateAction<string | null>>;
}

interface SettingsOverridesEditSession {
  isEditing: boolean;
  originalOverrides: SettingsOverrides;
  draftOverrides: SettingsOverrides;
  setDraftOverrides: Dispatch<SetStateAction<SettingsOverrides>>;
  newFileName: string;
  setNewFileName: Dispatch<SetStateAction<string>>;
  addFileError: string | null;
  setAddFileError: Dispatch<SetStateAction<string | null>>;
  draftChanged: boolean;
  draftErrors: Map<string, string>;
  attributionUsername: string;
  startEditing: () => void;
  stopEditing: () => void;
  previewChanges: () => void;
}

export function SettingsOverridesSection({
  cluster,
  onOperationCreated,
}: {
  cluster: Cluster;
  onOperationCreated?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftOverrides, setDraftOverrides] = useState<SettingsOverrides>({});
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [newFileName, setNewFileName] = useState("");
  const [addFileError, setAddFileError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [attributionUsername, setAttributionUsername] = useState("user");
  const [capturedResourceVersion, setCapturedResourceVersion] = useState<
    string | null | undefined
  >(undefined);

  const settingsOverrides = cluster.settings_overrides;
  const originalOverrides = useMemo(
    () => ({ ...(settingsOverrides ?? {}) }),
    [settingsOverrides],
  );
  const fileNames = useMemo(
    () => getSettingsOverrideFiles(originalOverrides),
    [originalOverrides],
  );
  const countLabel = `${countSettingsOverrides(originalOverrides)} settings`;
  const draftChanged = !settingsOverridesEqual(
    originalOverrides,
    draftOverrides,
  );
  const draftErrors = useMemo(
    () => (isEditing ? validateSettingsOverrides(draftOverrides) : new Map()),
    [isEditing, draftOverrides],
  );
  const canCreateSetConfig = Boolean(cluster.possible_operations.set_config);

  useEffect(() => {
    if (!isEditing) {
      setDraftOverrides(originalOverrides);
    }
  }, [isEditing, originalOverrides]);

  useEffect(() => {
    const username = getDisplayUsername();
    if (username) {
      setAttributionUsername(username);
    }
  }, []);

  const startEditing = () => {
    setDraftOverrides(originalOverrides);
    setCapturedResourceVersion(cluster.settings_overrides_resource_version);
    setSelectedFile((current) =>
      current != null && fileNames.includes(current)
        ? current
        : (fileNames[0] ?? null),
    );
    setAddFileError(null);
    setSubmitError(null);
    setIsEditing(true);
    setExpanded(true);
  };

  const stopEditing = () => {
    setIsEditing(false);
    setDraftOverrides(originalOverrides);
    setNewFileName("");
    setAddFileError(null);
    setSubmitError(null);
    setPreviewOpen(false);
  };

  const createOperation = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await api.createClusterOperation(
        cluster.id,
        buildSetConfigCreateRequestBody(draftOverrides, cluster, {
          resourceVersion: capturedResourceVersion,
        }),
      );
      setPreviewOpen(false);
      setIsEditing(false);
      onOperationCreated?.();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Failed to create operation",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const fileSelection: SettingsOverridesFileSelection = {
    selectedFile,
    setSelectedFile,
  };

  const editSession: SettingsOverridesEditSession = {
    isEditing,
    originalOverrides,
    draftOverrides,
    setDraftOverrides,
    newFileName,
    setNewFileName,
    addFileError,
    setAddFileError,
    draftChanged,
    draftErrors,
    attributionUsername,
    startEditing,
    stopEditing,
    previewChanges: () => {
      setSubmitError(null);
      setPreviewOpen(true);
    },
  };

  return (
    <div>
      <h4 className="mb-2 text-sm font-medium text-gray-900">
        Settings Overrides
      </h4>
      <SettingsOverridesToggle
        expanded={expanded}
        countLabel={countLabel}
        onToggle={() => setExpanded(!expanded)}
      />
      {expanded ? (
        <SettingsOverridesPanel
          settingsOverrides={settingsOverrides}
          fileNames={fileNames}
          canCreateSetConfig={canCreateSetConfig}
          editSession={editSession}
          fileSelection={fileSelection}
        />
      ) : null}
      <SettingsOverridesPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        originalOverrides={originalOverrides}
        draftOverrides={draftOverrides}
        canCreateSetConfig={canCreateSetConfig}
        isSubmitting={isSubmitting}
        submitError={submitError}
        onCreateOperation={createOperation}
      />
    </div>
  );
}

function SettingsOverridesToggle({
  expanded,
  countLabel,
  onToggle,
}: {
  expanded: boolean;
  countLabel: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "flex w-full items-center justify-between rounded-md border px-3 py-2 text-left transition-colors",
        "cursor-pointer border-gray-200 bg-gray-100 hover:bg-gray-200",
        expanded && "ring-2 ring-blue-500 ring-offset-1",
      )}
    >
      <div className="flex items-center gap-2">
        <Icon
          name="ChevronDown"
          size={14}
          className={cn(
            "text-gray-500 transition-transform",
            !expanded && "-rotate-90",
          )}
        />
        <span className="text-sm font-medium text-gray-700">
          {expanded ? "Hide" : "Show"} settings overrides
        </span>
      </div>
      <span className="text-xs text-gray-500">{countLabel}</span>
    </button>
  );
}

function SettingsOverridesPanel({
  settingsOverrides,
  fileNames,
  canCreateSetConfig,
  editSession,
  fileSelection,
}: {
  settingsOverrides: Cluster["settings_overrides"];
  fileNames: string[];
  canCreateSetConfig: boolean;
  editSession: SettingsOverridesEditSession;
  fileSelection: SettingsOverridesFileSelection;
}) {
  const sourceLabel =
    settingsOverrides == null
      ? "No turbopuffer-settings-overrides ConfigMap reported by latest heartbeat."
      : "Content of the turbopuffer-settings-overrides ConfigMap as of last heartbeat";

  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-white p-3">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="space-y-1 text-xs text-gray-500">
          <p>{sourceLabel}</p>
          <p>
            These overrides are meant to be temporary, and should eventually be
            tracked and persisted in Git.
          </p>
        </div>
        <SettingsOverridesActions
          editSession={editSession}
          hasOverrideFiles={fileNames.length > 0}
          canCreateSetConfig={canCreateSetConfig}
        />
      </div>
      <SettingsOverridesBody
        settingsOverrides={settingsOverrides}
        fileNames={fileNames}
        canCreateSetConfig={canCreateSetConfig}
        editSession={editSession}
        fileSelection={fileSelection}
      />
    </div>
  );
}

function SettingsOverridesActions({
  editSession,
  hasOverrideFiles,
  canCreateSetConfig,
}: {
  editSession: SettingsOverridesEditSession;
  hasOverrideFiles: boolean;
  canCreateSetConfig: boolean;
}) {
  if (editSession.isEditing) {
    const hasErrors = editSession.draftErrors.size > 0;
    return (
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={
            !editSession.draftChanged || !canCreateSetConfig || hasErrors
          }
          title={hasErrors ? "Fix the invalid YAML first" : undefined}
          onClick={editSession.previewChanges}
        >
          Preview changes
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="h-7 px-2 text-xs"
          onClick={editSession.stopEditing}
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs"
        onClick={editSession.startEditing}
      >
        {hasOverrideFiles ? "Edit" : "Add overrides"}
      </Button>
    </div>
  );
}

function SettingsOverridesBody({
  settingsOverrides,
  fileNames,
  canCreateSetConfig,
  editSession,
  fileSelection,
}: {
  settingsOverrides: Cluster["settings_overrides"];
  fileNames: string[];
  canCreateSetConfig: boolean;
  editSession: SettingsOverridesEditSession;
  fileSelection: SettingsOverridesFileSelection;
}) {
  return editSession.isEditing ? (
    <SettingsOverridesEditor
      originalOverrides={editSession.originalOverrides}
      draftOverrides={editSession.draftOverrides}
      setDraftOverrides={editSession.setDraftOverrides}
      selectedFile={fileSelection.selectedFile}
      setSelectedFile={fileSelection.setSelectedFile}
      newFileName={editSession.newFileName}
      setNewFileName={editSession.setNewFileName}
      addFileError={editSession.addFileError}
      setAddFileError={editSession.setAddFileError}
      canCreateSetConfig={canCreateSetConfig}
      validationErrors={editSession.draftErrors}
      attributionUsername={editSession.attributionUsername}
    />
  ) : (
    <SettingsOverridesViewer
      settingsOverrides={settingsOverrides}
      fileNames={fileNames}
      selectedFile={fileSelection.selectedFile}
      setSelectedFile={fileSelection.setSelectedFile}
    />
  );
}

export function SettingsOverridesViewer({
  settingsOverrides,
  fileNames,
  selectedFile,
  setSelectedFile,
}: {
  settingsOverrides: Cluster["settings_overrides"];
  fileNames: string[];
  selectedFile: string | null;
  setSelectedFile: Dispatch<SetStateAction<string | null>>;
}) {
  useEffect(() => {
    if (fileNames.length === 0) {
      setSelectedFile(null);
      return;
    }

    if (!selectedFile || !fileNames.includes(selectedFile)) {
      setSelectedFile(fileNames[0]);
    }
  }, [fileNames, selectedFile, setSelectedFile]);

  if (settingsOverrides == null) {
    return null;
  }

  if (fileNames.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        No settings overrides are currently set.
      </div>
    );
  }

  const activeFile = selectedFile ?? fileNames[0];

  return (
    <div className="space-y-3">
      <div
        className="flex gap-1 overflow-x-auto border-b border-gray-200"
        role="tablist"
        aria-label="Settings override files"
      >
        {fileNames.map((fileName) => {
          const isSelected = fileName === activeFile;
          return (
            <button
              key={fileName}
              type="button"
              role="tab"
              aria-selected={isSelected}
              onClick={() => setSelectedFile(fileName)}
              className={cn(
                "whitespace-nowrap border-b-2 px-3 py-2 text-xs font-medium transition-colors",
                isSelected
                  ? "border-blue-500 text-blue-700"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
              )}
            >
              {fileName}
            </button>
          );
        })}
      </div>
      <YamlCodeBlock value={settingsOverrides[activeFile] ?? ""} />
    </div>
  );
}
